import { Shield, Lock, Eye, Server } from 'lucide-react';

export default function Privacy() {
  const highlights = [
    { icon: Lock, title: 'End-to-End Encryption', desc: 'All data encrypted in transit and at rest' },
    { icon: Shield, title: 'HIPAA Compliant', desc: 'Full compliance with healthcare regulations' },
    { icon: Eye, title: 'Transparent Practices', desc: 'Clear policies on data usage' },
    { icon: Server, title: 'Secure Infrastructure', desc: 'SOC 2 Type II certified systems' },
  ];

  return (
    <div className="min-h-screen bg-[#030712]">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex p-4 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <Shield className="w-12 h-12 text-cyan-400" />
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
            Privacy Policy
          </h1>
          <p className="text-gray-400 text-lg">
            Last Updated: February 23, 2026
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="relative z-10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {highlights.map((item, index) => (
              <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <item.icon className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-gray-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-3xl p-8 lg:p-12">
            
            {/* Notice Box */}
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-2xl p-6 mb-12">
              <p className="text-gray-300 leading-relaxed">
                At PulseNex, we take your privacy seriously. This Privacy Policy explains how we collect,
                use, disclose, and safeguard your information when you use our services.
              </p>
            </div>

            <div className="space-y-12 text-gray-400">
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-bold">1</span>
                  Information We Collect
                </h2>
                <p className="mb-4 leading-relaxed">We collect information that you provide directly to us, including:</p>
                <ul className="space-y-3">
                  {[
                    'Personal identification information (name, email address, phone number)',
                    'Professional information (organization, job title)',
                    'Account credentials and preferences',
                    'Medical data processed through our AI systems (in accordance with HIPAA)',
                    'Usage data and analytics'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-bold">2</span>
                  How We Use Your Information
                </h2>
                <p className="mb-4 leading-relaxed">We use the information we collect to:</p>
                <ul className="space-y-3">
                  {[
                    'Provide, maintain, and improve our services',
                    'Process your transactions and send related information',
                    'Send you technical notices, updates, and support messages',
                    'Respond to your comments, questions, and customer service requests',
                    'Develop new products and services',
                    'Monitor and analyze trends, usage, and activities'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-bold">3</span>
                  HIPAA Compliance
                </h2>
                <p className="leading-relaxed">
                  As a healthcare technology provider, we are committed to HIPAA compliance. We implement
                  appropriate administrative, physical, and technical safeguards to protect Protected Health
                  Information (PHI). We maintain Business Associate Agreements (BAAs) with covered entities
                  and ensure that all PHI is encrypted both in transit and at rest.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-bold">4</span>
                  Data Security
                </h2>
                <p className="mb-4 leading-relaxed">We implement industry-standard security measures to protect your information:</p>
                <ul className="space-y-3">
                  {[
                    'End-to-end encryption for data transmission',
                    'Encrypted data storage with AES-256',
                    'Regular security audits and penetration testing',
                    'Access controls and multi-factor authentication',
                    '24/7 security monitoring and incident response'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-bold">5</span>
                  Data Retention
                </h2>
                <p className="leading-relaxed">
                  We retain your information for as long as necessary to provide our services and comply with
                  legal obligations. Medical data is retained in accordance with HIPAA requirements and
                  applicable state laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-bold">6</span>
                  Your Rights
                </h2>
                <p className="mb-4 leading-relaxed">You have the right to:</p>
                <ul className="space-y-3">
                  {[
                    'Access and receive a copy of your personal information',
                    'Request correction of inaccurate information',
                    'Request deletion of your information (subject to legal requirements)',
                    'Opt-out of marketing communications',
                    'Request restrictions on processing of your information'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-sm font-bold">7</span>
                  Contact Us
                </h2>
                <p className="mb-6 leading-relaxed">If you have questions about this Privacy Policy, please contact us:</p>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-white font-semibold mb-3">PulseNex Privacy Team</p>
                  <div className="space-y-2 text-gray-400">
                    <p>Email: <a href="mailto:privacy@pulsnex.com" className="text-cyan-400 hover:underline">privacy@pulsnex.com</a></p>
                    <p>Phone: <a href="tel:6302378000" className="text-cyan-400 hover:underline">(630) 237-8000</a></p>
                    <p>Address: 111 N Wabash Ave, Ste 100, Chicago, IL 60602</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
